import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Nullable,
  ParsedTournament,
  Tournament,
  TournamentStatus,
  TournamentType,
  UserWithToken,
  WithData,
} from '@ygoargentina/sdk/api/api.types';

export type LimitlessPendingStatus = 'PENDING' | 'IMPORTED' | 'DISMISSED';

export type LimitlessPendingPlayer = {
  id: number;
  limitlessUsername: string;
  placing: number | null;
  wins: number | null;
  losses: number | null;
  ties: number | null;
  user: {
    id: number;
    name: string;
    lastName: string;
    cossyId: string | null;
    limitlessId: string | null;
  };
};

/** Por qué un torneo entró como posible duplicado de un pendiente. */
export type DuplicateMatchReason = 'PLAYERS' | 'ORGANIZER' | 'NAME';

export type LimitlessDuplicateCandidate = {
  tournament: {
    id: number;
    title: string;
    dateFrom: string | null;
    status: TournamentStatus;
  };
  matchedPlayers: number;
  pendingPlayers: number;
  coverage: number;
  nameSimilarity: number;
  reasons: DuplicateMatchReason[];
};

export type LimitlessPendingTournament = {
  id: number;
  limitlessId: string;
  name: string;
  detectedFormat: number | null;
  startDate: string;
  participants: number;
  status: LimitlessPendingStatus;
  detectedAt: string;
  processedAt: string | null;
  organizer: { id: number; name: string };
  tournament: Tournament | null;
  players: LimitlessPendingPlayer[];
  possibleDuplicates: LimitlessDuplicateCandidate[];
};

export type LimitlessDetectionResult = {
  scanned: number;
  matched: number;
  created: number;
  refreshed: number;
};

const PENDING_FIELDS = gql`
  fragment LimitlessPendingFields on LimitlessPendingTournament {
    id
    limitlessId
    name
    detectedFormat
    startDate
    participants
    status
    detectedAt
    processedAt
    organizer {
      id
      name
    }
    # Los campos extra son los que pide getTournamentDisplayStatus: sin ellos
    # un torneo importado que quedó "Sin Cerrar" se ve igual que uno cerrado.
    tournament {
      id
      title
      status
      dateFrom
      dateTo
      rounds
      currentRound
      tournamentType
      deletedAt
    }
    players {
      id
      limitlessUsername
      placing
      wins
      losses
      ties
      user {
        id
        name
        lastName
        cossyId
        limitlessId
      }
    }
    possibleDuplicates {
      tournament {
        id
        title
        dateFrom
        status
      }
      matchedPlayers
      pendingPlayers
      coverage
      nameSimilarity
      reasons
    }
  }
`;

export const getLimitlessPending = async (
  user: Nullable<UserWithToken>,
  status?: LimitlessPendingStatus
) =>
  getGraphQLQueryClient(user).request<WithData<LimitlessPendingTournament[]>>(
    gql`
      ${PENDING_FIELDS}
      query limitlessPendingTournaments($status: LimitlessPendingStatus) {
        data: limitlessPendingTournaments(status: $status) {
          ...LimitlessPendingFields
        }
      }
    `,
    { status: status ?? null }
  );

export const detectLimitlessTournaments = async (
  user: Nullable<UserWithToken>,
  days: number
) =>
  getGraphQLQueryClient(user).request<WithData<LimitlessDetectionResult>>(
    gql`
      mutation detectLimitlessTournaments($days: Int) {
        data: detectLimitlessTournaments(days: $days) {
          scanned
          matched
          created
          refreshed
        }
      }
    `,
    { days }
  );

export const dismissLimitlessPending = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<LimitlessPendingTournament>>(
    gql`
      ${PENDING_FIELDS}
      mutation dismissLimitlessPending($id: Int!) {
        data: dismissLimitlessPending(id: $id) {
          ...LimitlessPendingFields
        }
      }
    `,
    { id }
  );

export const linkLimitlessPending = async (
  user: Nullable<UserWithToken>,
  id: number,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<LimitlessPendingTournament>>(
    gql`
      ${PENDING_FIELDS}
      mutation linkLimitlessPending($id: Int!, $tournamentId: Int!) {
        data: linkLimitlessPending(id: $id, tournamentId: $tournamentId) {
          ...LimitlessPendingFields
        }
      }
    `,
    { id, tournamentId }
  );

export const importLimitlessPending = async (
  user: Nullable<UserWithToken>,
  id: number,
  /**
   * Torneo ya cargado sobre el que importar —el del calendario—. Sin esto el
   * API crea un torneo nuevo.
   */
  tournamentId?: number,
  /** Tipo del torneo nuevo; por defecto suizo. Se ignora con `tournamentId`. */
  tournamentType?: TournamentType
) =>
  getGraphQLQueryClient(user).request<
    WithData<{
      tournament: Tournament;
      pending: LimitlessPendingTournament;
      importData: Nullable<ParsedTournament>;
    }>
  >(
    gql`
      ${PENDING_FIELDS}
      mutation importLimitlessPending(
        $id: Int!
        $tournamentId: Int
        $tournamentType: TournamentType
      ) {
        data: importLimitlessPending(
          id: $id
          tournamentId: $tournamentId
          tournamentType: $tournamentType
        ) {
          tournament {
            id
            title
            limitlessId
          }
          pending {
            ...LimitlessPendingFields
          }
          importData
        }
      }
    `,
    { id, tournamentId, tournamentType }
  );
